/**
 * Emulator UI Context
 *
 * Provides shared state and callbacks for emulator UI components.
 * Uses a tab-based model with an explicit viewPosition for layout management.
 */
import { createContext, useContext, type PropsWithChildren } from "react";
import type { useCuttlefishAdb } from "../cuttlefish/hooks/useCuttlefishAdb";
import type { CuttlefishConnectionFailure } from "../cuttlefish/types";
import type { EmulatorTab, ViewPosition } from "./useEmulatorTabState";

export interface EmulatorUIState {
  // Emulator info
  emulatorId: string;
  host?: string;

  // Connection state
  connecting?: boolean;
  /** Distinct connection failure class (failed / no-video / turn-unreachable). */
  connectionError?: CuttlefishConnectionFailure | null;
  adb?: ReturnType<typeof useCuttlefishAdb>;

  // Restart state
  onRestart?: () => void;
  onRestartClick?: () => void;
  isRestarting?: boolean;
  disableRestart?: boolean;

  // Tab panel state
  activeTab?: EmulatorTab;
  setActiveTab?: (tab: EmulatorTab) => void;
  hasViewedLogs?: boolean;

  // Panel view position
  viewPosition?: ViewPosition;
  setViewPosition?: (pos: ViewPosition) => void;

  /**
   * Restricts which position buttons are shown in the panel toolbar.
   * Omit (undefined) to show all three (bottom / right / popout).
   * Pass ['right'] in vscode-ext to hide the switcher entirely.
   */
  allowedPositions?: ViewPosition[];

  // Panel size state
  panelHeight?: number;
  setPanelHeight?: (height: number) => void;
  rightPanelWidth?: number;
  setRightPanelWidth?: (width: number) => void;

  // Debug pane state
  debugPaneOpen?: boolean;
  setDebugPaneOpen?: (open: boolean) => void;
  debugPaneWidth?: number;
  setDebugPaneWidth?: (width: number) => void;
  debugPaneHeight?: number;
  setDebugPaneHeight?: (height: number) => void;

  // Controls configuration
  initialDriverName?: string;

  // Optional callback invoked for each emulator log line received
  onLogLine?: (line: string) => void;
}

const EmulatorUIContext = createContext<EmulatorUIState | null>(null);

export interface EmulatorUIProviderProps extends PropsWithChildren {
  value: EmulatorUIState;
}

export function EmulatorUIProvider({
  children,
  value,
}: EmulatorUIProviderProps) {
  return (
    <EmulatorUIContext.Provider value={value}>
      {children}
    </EmulatorUIContext.Provider>
  );
}

export function useEmulatorUI(): EmulatorUIState {
  const context = useContext(EmulatorUIContext);
  if (!context) {
    throw new Error("useEmulatorUI must be used within EmulatorUIProvider");
  }
  return context;
}

/** Returns the context value, or null if not inside an EmulatorUIProvider. */
export function useEmulatorUIOptional(): EmulatorUIState | null {
  return useContext(EmulatorUIContext);
}
